import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import './media/bannerpart.css.js';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { addDisposableListener, EventType, clearNode, $, append, asCSSUrl } from 'vscode/vscode/vs/base/browser/dom';
import { ActionBar } from 'vscode/vscode/vs/base/browser/ui/actionbar/actionbar';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { IThemeService } from 'vscode/vscode/vs/platform/theme/common/themeService.service';
import { ThemeIcon } from 'vscode/vscode/vs/base/common/themables';
import { Part } from 'vscode/vscode/vs/workbench/browser/part';
import { IWorkbenchLayoutService } from 'vscode/vscode/vs/workbench/services/layout/browser/layoutService.service';
import { Action } from 'vscode/vscode/vs/base/common/actions';
import { Link } from 'vscode/vscode/vs/platform/opener/browser/link';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { IBannerService } from 'vscode/vscode/vs/workbench/services/banner/browser/bannerService.service';
import { MarkdownRenderer } from 'vscode/vscode/vs/editor/browser/widget/markdownRenderer/browser/markdownRenderer';
import { Action2, registerAction2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { Categories } from 'vscode/vscode/vs/platform/action/common/actionCommonCategories';
import { KeybindingsRegistry } from 'vscode/vscode/vs/platform/keybinding/common/keybindingsRegistry';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { widgetClose } from 'vscode/vscode/vs/platform/theme/common/iconRegistry';
import { BannerFocused } from 'vscode/vscode/vs/workbench/common/contextkeys';

const _moduleId = "vs/workbench/browser/parts/banner/bannerPart";
let BannerPart = class BannerPart extends Part {
    get minimumHeight() {
        return this.visible ? this.height : 0;
    }
    get maximumHeight() {
        return this.visible ? this.height : 0;
    }
    get onDidChange() { return this._onDidChangeSize.event; }
    constructor(themeService, layoutService, storageService, contextKeyService, instantiationService) {
        super("workbench.parts.banner" , { hasTitle: false }, themeService, storageService, layoutService);
        this.contextKeyService = contextKeyService;
        this.instantiationService = instantiationService;
        this.height = 26;
        this.minimumWidth = 0;
        this.maximumWidth = Number.POSITIVE_INFINITY;
        this._onDidChangeSize = this._register(( (new Emitter())));
        this.visible = false;
        this.focusedActionIndex = -1;
        this.markdownRenderer = this.instantiationService.createInstance(MarkdownRenderer, {});
    }
    createContentArea(parent) {
        this.element = parent;
        this.element.tabIndex = 0;
        this._register(addDisposableListener(this.element, EventType.FOCUS, () => {
            if (this.focusedActionIndex !== -1) {
                this.focusActionLink();
            }
        }));
        const scopedContextKeyService = this._register(this.contextKeyService.createScoped(this.element));
        BannerFocused.bindTo(scopedContextKeyService).set(true);
        return this.element;
    }
    close(item) {
        this.setVisibility(false);
        clearNode(this.element);
        if (typeof item.onClose === 'function') {
            item.onClose();
        }
        this.item = undefined;
    }
    focusActionLink() {
        const length = this.item?.actions?.length ?? 0;
        if (this.focusedActionIndex < length) {
            const actionLink = this.messageActionsContainer?.children[this.focusedActionIndex];
            if (actionLink instanceof HTMLElement) {
                this.actionBar?.setFocusable(false);
                actionLink.focus();
            }
        }
        else {
            this.actionBar?.focus(0);
        }
    }
    getAriaLabel(item) {
        if (item.ariaLabel) {
            return item.ariaLabel;
        }
        if (typeof item.message === 'string') {
            return item.message;
        }
        return undefined;
    }
    getBannerMessage(message) {
        if (typeof message === 'string') {
            const element = $('span');
            element.innerText = message;
            return element;
        }
        return this.markdownRenderer.render(message).element;
    }
    setVisibility(visible) {
        if (visible !== this.visible) {
            this.visible = visible;
            this.focusedActionIndex = -1;
            this.layoutService.setPartHidden(!visible, "workbench.parts.banner" );
            this._onDidChangeSize.fire(undefined);
        }
    }
    focus() {
        this.focusedActionIndex = -1;
        this.element.focus();
    }
    focusNextAction() {
        const length = this.item?.actions?.length ?? 0;
        this.focusedActionIndex = this.focusedActionIndex < length ? this.focusedActionIndex + 1 : 0;
        this.focusActionLink();
    }
    focusPreviousAction() {
        const length = this.item?.actions?.length ?? 0;
        this.focusedActionIndex = this.focusedActionIndex > 0 ? this.focusedActionIndex - 1 : length;
        this.focusActionLink();
    }
    hide(id) {
        if (this.item?.id !== id) {
            return;
        }
        this.setVisibility(false);
    }
    show(item) {
        if (item.id === this.item?.id) {
            this.setVisibility(true);
            return;
        }
        clearNode(this.element);
        const ariaLabel = this.getAriaLabel(item);
        if (ariaLabel) {
            this.element.setAttribute('aria-label', ariaLabel);
        }
        const iconContainer = append(this.element, $('div.icon-container'));
        iconContainer.setAttribute('aria-hidden', 'true');
        if (ThemeIcon.isThemeIcon(item.icon)) {
            iconContainer.appendChild($(`div${ThemeIcon.asCSSSelector(item.icon)}`));
        }
        else {
            iconContainer.classList.add('custom-icon');
            if (URI.isUri(item.icon)) {
                iconContainer.style.backgroundImage = asCSSUrl(item.icon);
            }
        }
        const messageContainer = append(this.element, $('div.message-container'));
        messageContainer.setAttribute('aria-hidden', 'true');
        messageContainer.appendChild(this.getBannerMessage(item.message));
        this.messageActionsContainer = append(this.element, $('div.message-actions-container'));
        if (item.actions) {
            for (const action of item.actions) {
                this._register(this.instantiationService.createInstance(Link, this.messageActionsContainer, { ...action, tabIndex: -1 }, {}));
            }
        }
        const actionBarContainer = append(this.element, $('div.action-container'));
        this.actionBar = this._register(( (new ActionBar(actionBarContainer))));
        const label = item.closeLabel ?? 'Close Banner';
        const closeAction = this._register(( (new Action(
            'banner.close',
            label,
            ThemeIcon.asClassName(widgetClose),
            true,
            () => this.close(item)
        ))));
        this.actionBar.push(closeAction, { icon: true, label: false });
        this.actionBar.setFocusable(false);
        this.setVisibility(true);
        this.item = item;
    }
    toJSON() {
        return {
            type: "workbench.parts.banner"
        };
    }
};
BannerPart = ( (__decorate([
    ( (__param(0, IThemeService))),
    ( (__param(1, IWorkbenchLayoutService))),
    ( (__param(2, IStorageService))),
    ( (__param(3, IContextKeyService))),
    ( (__param(4, IInstantiationService)))
], BannerPart)));
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: 'workbench.banner.focusBanner',
    weight: 200 ,
    primary: 9 ,
    when: BannerFocused,
    handler: (accessor) => {
        const bannerService = accessor.get(IBannerService);
        bannerService.focus();
    }
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: 'workbench.banner.focusNextAction',
    weight: 200 ,
    primary: 17 ,
    secondary: [18 ],
    when: BannerFocused,
    handler: (accessor) => {
        const bannerService = accessor.get(IBannerService);
        bannerService.focusNextAction();
    }
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: 'workbench.banner.focusPreviousAction',
    weight: 200 ,
    primary: 15 ,
    secondary: [16 ],
    when: BannerFocused,
    handler: (accessor) => {
        const bannerService = accessor.get(IBannerService);
        bannerService.focusPreviousAction();
    }
});
class FocusBannerAction extends Action2 {
    static { this.ID = 'workbench.action.focusBanner'; }
    static { this.LABEL = ( localize2WithPath(_moduleId, 0, "Focus Banner")); }
    constructor() {
        super({
            id: FocusBannerAction.ID,
            title: FocusBannerAction.LABEL,
            category: Categories.View,
            f1: true
        });
    }
    async run(accessor) {
        const layoutService = accessor.get(IWorkbenchLayoutService);
        layoutService.focusPart("workbench.parts.banner" );
    }
}
registerAction2(FocusBannerAction);

export { BannerPart };
